import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  InlineGrid,
  Text,
  SkeletonBodyText,
  EmptyState,
  BlockStack,
  TextField,
  InlineStack,
  Button,
  Box,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';

export default function ClinicReports() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedStart, setAppliedStart] = useState<string | undefined>(undefined);
  const [appliedEnd, setAppliedEnd] = useState<string | undefined>(undefined);

  const hasDateRange = !!appliedStart && !!appliedEnd;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['clinic-report-summary', appliedStart, appliedEnd],
    queryFn: () =>
      clinicPortalApi.getReportSummary({
        startDate: appliedStart,
        endDate: appliedEnd,
      }),
    enabled: hasDateRange,
  });

  const handleApply = () => {
    setAppliedStart(startDate || undefined);
    setAppliedEnd(endDate || undefined);
    refetch();
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStart(undefined);
    setAppliedEnd(undefined);
  };

  if (error) {
    return (
      <Page title="Reports">
        <Card>
          <EmptyState
            heading="Error loading report"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Reports">
      <BlockStack gap="500">
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">Date Range</Text>
            <InlineStack gap="400" align="start">
              <TextField
                label="Start date"
                type="date"
                value={startDate}
                onChange={setStartDate}
                autoComplete="off"
              />
              <TextField
                label="End date"
                type="date"
                value={endDate}
                onChange={setEndDate}
                autoComplete="off"
              />
            </InlineStack>
            <InlineStack gap="200">
              <Button variant="primary" onClick={handleApply}>Apply</Button>
              <Button onClick={handleClear}>Clear</Button>
            </InlineStack>
          </BlockStack>
        </Card>

        {isLoading ? (
          <Card>
            <SkeletonBodyText lines={6} />
          </Card>
        ) : data ? (
          <InlineGrid columns={3} gap="400">
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodyMd">Total Orders</Text>
                <Text as="p" variant="heading2xl">{data.orderCount}</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodyMd">Revenue</Text>
                <Text as="p" variant="heading2xl">
                  ${data.revenue.toFixed(2)}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodyMd">Avg Order Value</Text>
                <Text as="p" variant="heading2xl">
                  ${data.avgOrderValue.toFixed(2)}
                </Text>
              </BlockStack>
            </Card>
          </InlineGrid>
        ) : (
          <Box>
            <Text as="p" tone="subdued">Apply a date range to view the report summary.</Text>
          </Box>
        )}
      </BlockStack>
    </Page>
  );
}
